// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file TopNavMegaMenu.tsx
 * @input Uses React, StyleX, usePopover (Popover API + CSS anchor positioning)
 * @output Exports TopNavMegaMenu component and related types
 * @position Navigation item with hover-triggered full-width mega menu for TopNav
 *
 * Uses usePopover to promote the panel to the top layer via the Popover API,
 * eliminating z-index stacking. CSS anchor positioning places the panel below
 * the nav wrapper.
 *
 * Supports three render modes via TopNavRenderContext:
 * - 'default': desktop popover mega menu (hover/click triggered)
 * - 'mobile-bar': returns null (hidden in compact mobile bar)
 * - 'drawer': drill-down navigation with back button
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/TopNav/TopNav.doc.mjs
 * - /packages/core/src/TopNav/index.ts
 * - /packages/cli/templates/blocks/components/TopNav/ (showcase blocks)
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, durationVars, easeVars, fontWeightVars, shadowVars, typeScaleVars, borderVars } from "../theme/tokens.stylex.js";
import { usePopover } from "../Popover/usePopover.js";
import { Grid } from "../Grid/Grid.js";
import { getIcon } from "../Icon/globalIconRegistry.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { navItemStyles } from "../NavItem/navItemStyles.stylex.js";
import { useTopNavSlot } from "./TopNavContext.js";
import { useTopNavRenderMode } from "./TopNavRenderContext.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Styles
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const styles = {
  panelAnimation: {
    kSiTet: "xg01cxk xofkqq2",
    k3aq6I: "x1bvilyr x9cjr8z",
    k1ekBW: "x4bbghf",
    kIyJzY: "xgneliz",
    kAMwcw: "xlr8y92",
    kzIqYQ: "xd00j3c",
    kamtoy: "x4itv7f",
    k1tdAh: "x12p7p72",
    $$css: true
  },
  drawerHeader: {
    kjj79g: "x1qughib",
    $$css: true
  }
};

// =============================================================================
// Types
// =============================================================================

// =============================================================================
// TopNavMegaMenu
// =============================================================================

/**
 * A navigation item that displays a full-width mega menu on hover.
 *
 * Uses a composed children API with sub-components:
 * - `items` — ReactNode slot, typically TopNavMegaMenuItem components
 * - `featured` — ReactNode slot for the right-panel / drawer featured card
 *
 * Supports three render modes via TopNavRenderContext:
 * - `'default'`: desktop popover with hover/click trigger
 * - `'mobile-bar'`: hidden (returns null)
 * - `'drawer'`: inline collapsible matching TopNavMenu pattern
 *
 * @example
 * ```
 * <TopNav
 *   startContent={
 *     <TopNavMegaMenu
 *       label="Products"
 *       items={
 *         <>
 *           <TopNavMegaMenuItem
 *             title="Analytics"
 *             description="Track behavior"
 *             icon={<ChartIcon />}
 *             href="/analytics"
 *           />
 *           <TopNavMegaMenuItem
 *             title="Messaging"
 *             description="Real-time comms"
 *             icon={<ChatIcon />}
 *             href="/messaging"
 *           />
 *         </>
 *       }
 *       featured={
 *         <>
 *           <strong>New: AI Features</strong>
 *           <p>Explore our latest AI-powered tools.</p>
 *         </>
 *       }
 *     />
 *   }
 * />
 * ```
 */
export function TopNavMegaMenu({
  ref,
  label,
  items,
  featured,
  delay = 150,
  hideDelay = 250,
  onOpenChange
}) {
  const renderMode = useTopNavRenderMode();

  // =========================================================================
  // Mobile-bar mode — hidden
  // =========================================================================
  if (renderMode === 'mobile-bar') {
    return null;
  }

  // =========================================================================
  // Drawer mode — inline collapsible
  // =========================================================================
  if (renderMode === 'drawer') {
    return /*#__PURE__*/_jsx(DrawerMegaMenu, {
      label: label,
      items: items,
      featured: featured
    });
  }

  // =========================================================================
  // Default mode — desktop popover
  // =========================================================================
  return /*#__PURE__*/_jsx(DefaultMegaMenu, {
    ref: ref,
    label: label,
    items: items,
    featured: featured,
    delay: delay,
    hideDelay: hideDelay,
    onOpenChange: onOpenChange
  });
}
TopNavMegaMenu.displayName = 'TopNavMegaMenu';

// =============================================================================
// DefaultMegaMenu — desktop popover mode
// =============================================================================

function DefaultMegaMenu({
  ref,
  label,
  items,
  featured,
  delay = 150,
  hideDelay = 250,
  onOpenChange
}) {
  const slot = useTopNavSlot();
  const showTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const triggerButtonRef = useRef(null);
  const clickLockedRef = useRef(false);
  const handlePopoverShow = useCallback(() => {
    onOpenChange?.(true);
  }, [onOpenChange]);
  const handlePopoverHide = useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);
  const popover = usePopover({
    dialogLabel: label,
    // hasSurface: false — mega menu provides its own surface (panelContainer)
    // with border-top and custom overflow. Animation is applied via the
    // render() call's xstyle prop (panelAnimation), not the hook options.
    hasSurface: false,
    onShow: handlePopoverShow,
    onHide: handlePopoverHide
  });

  // Set the CSS anchor to the parent <nav> element (the TopNav).
  useEffect(() => {
    const nav = triggerButtonRef.current?.closest('nav');
    if (nav) {
      popover.triggerRef(nav);
    }
    return () => {
      popover.triggerRef(null);
    };
  }, [popover]);
  const clearTimeouts = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);
  const scheduleShow = useCallback(() => {
    clearTimeouts();
    showTimeoutRef.current = setTimeout(() => {
      popover.show({
        skipAutoFocus: true
      });
    }, delay);
  }, [clearTimeouts, popover, delay]);
  const scheduleHide = useCallback(() => {
    clearTimeouts();
    hideTimeoutRef.current = setTimeout(() => {
      popover.hide();
    }, hideDelay);
  }, [clearTimeouts, popover, hideDelay]);
  const handleMouseEnter = useCallback(() => {
    if (!clickLockedRef.current) {
      scheduleShow();
    }
  }, [scheduleShow]);
  const handleMouseLeave = useCallback(() => {
    if (!clickLockedRef.current) {
      scheduleHide();
    }
  }, [scheduleHide]);
  const handleClick = useCallback(() => {
    clearTimeouts();
    if (popover.isOpen) {
      clickLockedRef.current = false;
      popover.hide();
      triggerButtonRef.current?.focus();
    } else {
      clickLockedRef.current = true;
      popover.show();
    }
  }, [popover, clearTimeouts]);
  useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, [clearTimeouts]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("button", {
      ref: mergeRefs(triggerButtonRef, ref),
      type: "button",
      "aria-haspopup": "true",
      "aria-expanded": popover.isOpen,
      onClick: handleClick,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ...mergeProps(themeProps('top-nav-mega-menu'), {
        0: {
          className: "x3nfvp2 x6s0dn4 x1txdalj x1vofgu7 xrrkdod xh6dtrn xcr08ib x1kq96og x1e4wzip xv1l7n4 x1hl2dhg x1ypdohk xs2xxs2 xuedmi6 xlr8y92 xjbqb8w xe9uy6x x17nn4n9 x1wfwxd8 x7s97pk xjb2p0i"
        },
        1: {
          className: "x3nfvp2 x6s0dn4 x1txdalj x1vofgu7 xrrkdod xh6dtrn xcr08ib x1kq96og x1e4wzip x1hl2dhg x1ypdohk xs2xxs2 xuedmi6 xlr8y92 x17nn4n9 x1wfwxd8 x7s97pk xjb2p0i x1tgivj0 x1lmrjuc"
        }
      }[!!popover.isOpen << 0]),
      children: [label, /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "x3nfvp2 x6s0dn4 x11xpdln xuedmi6 xlr8y92"
          },
          1: {
            className: "x3nfvp2 x6s0dn4 x11xpdln xuedmi6 xlr8y92 x19jd1h0"
          }
        }[!!popover.isOpen << 0],
        children: getIcon('chevronDown')
      })]
    }), popover.render(/*#__PURE__*/_jsx("div", {
      role: "menu",
      "aria-label": label,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ...{
        className: "x1prclbq x11xkdxz x13fuv20 x1pc3f07 x1hviunn x1i5ehqx xb3r6kr"
      },
      children: /*#__PURE__*/_jsxs("div", {
        ...{
          className: "x78zum5 x1a02dak x1qh66ti x8o8v82 xrrkdod xyzno7u"
        },
        children: [items != null && /*#__PURE__*/_jsx("div", {
          ...{
            className: "xgyuaek xs83m0k xchdapg xeuugli"
          },
          children: /*#__PURE__*/_jsx(Grid, {
            columns: 2,
            gap: 2,
            children: items
          })
        }), featured != null && /*#__PURE__*/_jsx("div", {
          ...{
            className: "x1iyjqo2 xs83m0k xjpgo6f x1hviunn xwmxj5m xb3r6kr x78zum5 xdt5ytf"
          },
          children: featured
        })]
      })
    }), {
      placement: 'below',
      alignment: slot,
      xstyle: styles.panelAnimation
    })]
  });
}

// =============================================================================
// DrawerMegaMenu — mobile drawer inline collapsible mode
// =============================================================================

function DrawerMegaMenu({
  label,
  items,
  featured
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const menuId = `mega-menu-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return /*#__PURE__*/_jsxs("div", {
    ...{
      className: "x78zum5 xdt5ytf"
    },
    children: [/*#__PURE__*/_jsxs("button", {
      type: "button",
      onClick: () => setIsExpanded(v => !v),
      "aria-expanded": isExpanded,
      "aria-controls": `${menuId}-items`,
      ...mergeProps(themeProps('top-nav-mega-menu', {
        mode: 'drawer'
      }), stylex.props(navItemStyles.item, styles.drawerHeader)),
      children: [label, /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "x3nfvp2 x11xpdln xuedmi6 xlr8y92"
          },
          1: {
            className: "x3nfvp2 x11xpdln xuedmi6 xlr8y92 x19jd1h0"
          }
        }[!!isExpanded << 0],
        children: getIcon('chevronDown')
      })]
    }), /*#__PURE__*/_jsx("div", {
      id: `${menuId}-items`,
      ...{
        0: {
          className: "xrvj5dj xihq33y x1qn9uv2 x80gvsz xlr8y92"
        },
        1: {
          className: "xrvj5dj x1qn9uv2 x80gvsz xlr8y92 x1tu4anv"
        }
      }[!!isExpanded << 0],
      children: /*#__PURE__*/_jsxs("div", {
        ...{
          className: "xb3r6kr x2lwn1j"
        },
        children: [items, featured != null && /*#__PURE__*/_jsx("div", {
          ...{
            className: "xtbrsbv x1l10yog x1hviunn xwmxj5m xb3r6kr"
          },
          children: featured
        })]
      })
    })]
  });
}